import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["vehicleList", "selectedInput", "ruleTypeInput", "searchInput"]

  connect() {
    this.selectedVehicles = new Set()
    this.allVehicles = []
    this.isLoading = false
    
    this.loadExistingSelections()
    this.loadVehicles()
    this.updateRuleType()
  }

  // Helper para buscar traduções
  t(key) {
    const translations = window.I18n?.translations?.javascript?.electronic_fences || {}
    return translations[key] || key
  }

  loadExistingSelections() {
    const selectedInput = this.selectedInputTarget
    if (selectedInput?.value) {
      const vehicleIds = selectedInput.value
        .split(',')
        .map(id => parseInt(id.trim()))
        .filter(id => !isNaN(id))
      vehicleIds.forEach(id => this.selectedVehicles.add(id))
    }
  }

  async loadVehicles() {
    if (this.isLoading) return
    this.isLoading = true

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

      const response = await fetch('/vehicles/load_for_fence', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': csrfToken || ''
        },
        credentials: 'same-origin'
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const data = await response.json()
      this.allVehicles = data
      this.renderVehicles(data)

    } catch (error) {
      this.showError(error.message)
    } finally {
      this.isLoading = false
    }
  }

  showError(message) {
    if (this.vehicleListTarget) {
      this.vehicleListTarget.innerHTML = `
        <div class="flex flex-col items-center justify-center py-8 text-red-500">
          <svg class="w-8 h-8 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <span class="text-sm font-medium">${this.t('vehicle_loading_error')}</span>
          <button onclick="this.closest('[data-controller*=fence-vehicle-selector]').fenceVehicleSelector.loadVehicles()" class="mt-3 px-4 py-2 bg-blue-500 text-white rounded text-sm hover:bg-blue-600">
            ${this.t('try_again')}
          </button>
        </div>
      `
    }
  }

  renderVehicles(vehicles) {
    if (!this.vehicleListTarget) return

    if (vehicles.length === 0) {
      this.vehicleListTarget.innerHTML = `
        <div class="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400">
          <span class="text-sm font-semibold text-gray-900 dark:text-white">${this.t('no_vehicles_found')}</span>
        </div>
      `
      return
    }

    const fragment = document.createDocumentFragment()

    vehicles.forEach((vehicle, index) => {
      const isSelected = this.selectedVehicles.has(vehicle.id)
      const isLast = index === vehicles.length - 1

      const div = document.createElement('div')
      div.className = `relative group ${!isLast ? 'border-b border-gray-100 dark:border-gray-700' : ''}`

      div.innerHTML = `
        <label class="flex items-center px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
          <input type="checkbox"
                 value="${vehicle.id}"
                 ${isSelected ? 'checked' : ''}
                 data-vehicle-checkbox
                 class="mr-3 w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600">
          <div class="flex-1">
            <span class="font-semibold text-gray-900 dark:text-white text-sm block">
              ${vehicle.plate || this.t('no_plate')}
            </span>
            ${vehicle.model ? `<span class="text-xs text-gray-500 dark:text-gray-400">${vehicle.model}</span>` : ''}
          </div>
        </label>
      `

      const checkbox = div.querySelector('input[type="checkbox"]')
      checkbox.addEventListener('change', (e) => this.toggleVehicle(e))

      fragment.appendChild(div)
    })

    this.vehicleListTarget.innerHTML = ''
    this.vehicleListTarget.appendChild(fragment)
    this.updateHiddenInput()
  }

  search(event) {
    const query = event.target.value.toLowerCase().trim()

    if (query === '') {
      this.renderVehicles(this.allVehicles)
      return
    }

    const filtered = this.allVehicles.filter(vehicle => {
      const plate = (vehicle.plate || '').toLowerCase()
      const model = (vehicle.model || '').toLowerCase()
      return plate.includes(query) || model.includes(query)
    })

    this.renderVehicles(filtered)
  }

  selectAll() {
    this.allVehicles.forEach(vehicle => {
      this.selectedVehicles.add(vehicle.id)
    })

    const checkboxes = document.querySelectorAll('[data-vehicle-checkbox]')
    checkboxes.forEach(checkbox => {
      checkbox.checked = true
    })

    this.updateHiddenInput()
  }

  clearAll() {
    this.selectedVehicles.clear()

    const checkboxes = document.querySelectorAll('[data-vehicle-checkbox]')
    checkboxes.forEach(checkbox => {
      checkbox.checked = false
    })

    this.updateHiddenInput()
  }

  toggleVehicle(event) {
    const vehicleId = parseInt(event.target.value)

    if (event.target.checked) {
      this.selectedVehicles.add(vehicleId)
    } else {
      this.selectedVehicles.delete(vehicleId)
    }

    this.updateHiddenInput()
  }

  updateHiddenInput() {
    if (this.selectedInputTarget) {
      const selectedIds = Array.from(this.selectedVehicles)
      this.selectedInputTarget.value = selectedIds.join(',')
    }
  }

  updateRuleType() {
    const ruleTypeInputs = document.querySelectorAll('input[name="fence_rule_type"]')
    ruleTypeInputs.forEach(radio => {
      radio.addEventListener('change', () => {
        if (radio.checked && this.ruleTypeInputTarget) {
          this.ruleTypeInputTarget.value = radio.value
        }

        const electronicFenceElement = document.querySelector('[data-controller*="electronic-fence"]')
        if (electronicFenceElement && electronicFenceElement.electronicFence?.clearRuleTypeError) {
          electronicFenceElement.electronicFence.clearRuleTypeError()
        }
      })
    })
  }
}